import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '../../api'
import { cn } from '../../lib/utils'
import KpiCard from '../ui/KpiCard'
import { DashboardSkeleton } from '../finance/SkeletonStates'
import { MorningBriefing } from './MorningBriefing'
import { VarianceList } from './VarianceList'
import { AskAnything } from './AskAnything'
import { CloseReadiness } from './CloseReadiness'

interface IntelligenceModeProps {
  entityCode: string
  periodCode: string
  comparePeriod?: string
  onSwitchToExploration?: (metricCode: string) => void
  className?: string
}

const KEY_METRICS = [
  'revenue', 'net_income', 'gross_margin',
  'working_capital', 'dso', 'exception_count',
]

function getBenchmark(m: any): 'good' | 'warning' | 'critical' | undefined {
  const v = m?.numeric_value
  if (v == null) return undefined
  if (m.benchmark_good != null && v >= m.benchmark_good) return 'good'
  if (m.benchmark_critical != null && v <= m.benchmark_critical) return 'critical'
  if (m.benchmark_warning != null && v <= m.benchmark_warning) return 'warning'
  return undefined
}

export function IntelligenceMode({
  entityCode, periodCode, comparePeriod,
  onSwitchToExploration, className,
}: IntelligenceModeProps) {
  // Briefing (passes user-selected period/entity so it matches KPI cards)
  const { data: briefing, isLoading: briefingLoading } = useQuery({
    queryKey: ['briefing', periodCode, entityCode],
    queryFn: () => api.get('/metrics/workspace/briefing', {
      params: {
        period_code: periodCode || undefined,
        entity_code: entityCode,
      },
    }).then(r => r.data),
    staleTime: 60_000,
  })

  // Dashboard metrics
  const { data: dashboard, isLoading: metricsLoading } = useQuery({
    queryKey: ['dashboard-metrics', entityCode, periodCode],
    queryFn: () => api.get('/metrics/financial/dashboard', {
      params: { entity_code: entityCode, period_code: periodCode || undefined },
    }).then(r => r.data),
    enabled: !!periodCode,
  })

  // Variances
  const { data: varianceData, isLoading: varianceLoading } = useQuery({
    queryKey: ['variances', periodCode, entityCode],
    queryFn: () => api.get('/metrics/variance-dashboard', {
      params: { period_code: periodCode, entity_code: entityCode },
    }).then(r => r.data),
    enabled: !!periodCode,
  })

  // Suggested questions
  const { data: suggestedData } = useQuery({
    queryKey: ['suggested-questions'],
    queryFn: () => api.get('/metrics/workspace/suggested-questions').then(r => r.data),
    staleTime: 120_000,
  })

  // Proactive alerts (dry-run summary)
  const { data: alertData } = useQuery({
    queryKey: ['proactive-alerts'],
    queryFn: () => api.get('/alerts/summary').then(r => r.data),
    staleTime: 2 * 60_000,
    retry: 1,
  })

  // Auto-generate alerts once per hour (fire-and-forget, localStorage throttle)
  useEffect(() => {
    const lastGenerated = localStorage.getItem('alerts_last_generated')
    const now = Date.now()
    if (!lastGenerated || now - parseInt(lastGenerated) > 3_600_000) {
      api.post('/alerts/generate').catch(() => {})
      localStorage.setItem('alerts_last_generated', String(now))
    }
  }, [])

  const metrics = dashboard?.metrics || []
  const variances = varianceData?.variances || []
  const suggestedQuestions = suggestedData?.questions || []

  if (metricsLoading && !dashboard) {
    return <DashboardSkeleton className={className} />
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Proactive Alerts */}
      {alertData?.alerts?.length > 0 && (
        <div className="space-y-2">
          {alertData.alerts.map((alert: any, i: number) => (
            <div
              key={i}
              className={cn(
                'flex items-start gap-3 rounded-lg px-4 py-3 border',
                alert.priority === 'URGENT'
                  ? 'bg-red-500/10 border-red-500/20 text-red-400'
                  : 'bg-amber-500/10 border-amber-500/20 text-amber-400',
              )}
            >
              <span className="text-sm mt-0.5 shrink-0">
                {alert.priority === 'URGENT' ? '\u26A0' : '\u25B3'}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{alert.title}</p>
                <p className="text-xs opacity-80 mt-0.5">{alert.body}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Morning Briefing */}
      <MorningBriefing
        narrative={briefing?.narrative}
        attentionItems={briefing?.attention_items}
        timestamp={briefing?.generated_at}
        loading={briefingLoading}
      />

      {/* KPI Row */}
      <div>
        <div className="flex items-center gap-1.5 mb-3">
          <div className="h-1.5 w-1.5 rounded-full bg-[var(--accent,var(--accent))]" />
          <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
            Key Metrics
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {KEY_METRICS.map((code, i) => {
            const m = metrics.find((x: any) => x.metric_code === code)
            return (
              <div
                key={code}
                className="animate-[fadeIn_0.4s_ease_both]"
                style={{ animationDelay: `${i * 50}ms` }}
              >
                <KpiCard
                  label={m?.metric_name || code.replace(/_/g, ' ')}
                  value={m?.numeric_value ?? 0}
                  unit={m?.unit || 'USD'}
                  benchmark={m ? getBenchmark(m) : undefined}
                  onClick={onSwitchToExploration ? () => onSwitchToExploration(code) : undefined}
                  loading={metricsLoading}
                />
              </div>
            )
          })}
        </div>
      </div>

      {/* Close Readiness */}
      <CloseReadiness periodCode={periodCode} entityCode={entityCode} />

      {/* Significant Variances */}
      <VarianceList
        variances={variances}
        onDrill={onSwitchToExploration}
        loading={varianceLoading}
      />

      {/* Ask Anything */}
      <AskAnything suggestedQuestions={suggestedQuestions} />
    </div>
  )
}
